import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { absoluteUrl, localeFromPath } from '@/i18n/locale'
import { OG_IMAGE_RU, OG_IMAGE_EN, OG_IMAGE_ALT_RU, OG_IMAGE_ALT_EN } from '@/lib/og'
import { canonicalPath } from '@/lib/sections'

/**
 * SeoLocale — единственный источник title/description/OG/twitter для ГЛАВНОЙ
 * (/ и /en). Монтируется только в App (главная), поэтому задаёт теги
 * безусловно под локаль. Контентные страницы аналогичный набор берут из
 * PageTemplate.
 *
 * Задаёт:
 *   - <html lang="ru|en">
 *   - hreflang alternate ru ↔ en ↔ x-default
 *   - <title>, description, og:*, twitter:* под локаль
 *
 * Критично для превью в мессенджерах (Telegram/WhatsApp не исполняют JS,
 * читают пре-рендеренный <head>):
 *   - og:url — self-URL под локаль (на /en → /en, НЕ корень). Иначе Telegram
 *     считает канонической русскую главную и показывает русскую карточку.
 *   - og:image — локализованная (английская картинка на /en; сама картинка
 *     содержит текст, поэтому русская на /en = «превью на русском»). Адреса
 *     и версия — в src/lib/og.ts: имя файла версионное, иначе Telegram
 *     показывает закэшированную карточку с давно снятым оффером.
 * Статические дубли этих тегов убраны из index.html (2026-07-05).
 */
const HOME = {
  ru: {
    // Точная фраза запроса «внедрение ИИ в бизнес под ключ» — непрерывно и
    // в первой позиции; позиционирование («система управления») — следом,
    // в пределах обрезки выдачи. «ИИ-агенты» в title не ставим, чтобы не
    // каннибалить pillar /vnedrenie-ii-agentov (его ключ — «внедрение ии агентов»).
    title: 'Внедрение ИИ в бизнес под ключ — система управления | DNAI',
    // Description не повторяет title дословно: title держит запрос,
    // description — что именно человек получит, срок и условия оплаты. Длина
    // 150–170 знаков (ТЗ 4.1). Цены нет: на сайте её не показываем, стоимость
    // считаем на бесплатной диагностике (Тони 2026-09-27).
    desc: 'Система управления бизнесом, людьми и ИИ-агентами под ключ за 2–3 месяца: собственник видит всё в одном окне. Аванс 30\u00a0%, остальное — по приёмке этапов.',
    ogLocale: 'ru_RU',
    ogImage: OG_IMAGE_RU,
    ogImageAlt: OG_IMAGE_ALT_RU,
  },
  en: {
    title: 'Turnkey AI Implementation for Business: Management System | DNAI',
    // Цена в сниппете обязана совпадать с ценой на странице (OfferLadder),
    // иначе человек покупает сниппет и получает другое — возврат в выдачу.
    // Цены нет ни в сниппете, ни на странице (Тони 2026-09-27).
    desc: 'A management system for your business, people and AI agents, turnkey in 2–3 months, all in one window for the owner. 30% upfront, the rest on stage acceptance.',
    ogLocale: 'en_US',
    ogImage: OG_IMAGE_EN,
    ogImageAlt: OG_IMAGE_ALT_EN,
  },
}

export function SeoLocale() {
  // Адрес блока (/roi) для поисковика и соцсетей — та же главная:
  // hreflang и og:url сворачиваются в «/», иначе у одной страницы
  // появилось бы шесть адресов-двойников.
  const pathname = canonicalPath(useLocation().pathname)
  const locale = localeFromPath(pathname)
  const ruUrl = absoluteUrl(pathname, 'ru')
  const enUrl = absoluteUrl(pathname, 'en')
  const selfUrl = absoluteUrl(pathname, locale)
  const h = HOME[locale]

  // Плоский рендер (без условных фрагментов): Helmet стабильнее подхватывает
  // title/meta, когда они перечислены прямо, а не собраны тернарником.
  return (
    <Helmet>
      <html lang={locale} />
      <link rel="alternate" hrefLang="ru" href={ruUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={ruUrl} />
      <title>{h.title}</title>
      <meta name="description" content={h.desc} />
      <meta property="og:title" content={h.title} />
      <meta property="og:description" content={h.desc} />
      <meta property="og:url" content={selfUrl} />
      <meta property="og:locale" content={h.ogLocale} />
      <meta property="og:image" content={h.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={h.ogImageAlt} />
      <meta name="twitter:title" content={h.title} />
      <meta name="twitter:description" content={h.desc} />
      <meta name="twitter:image" content={h.ogImage} />
    </Helmet>
  )
}
